import React, { useEffect, useState } from 'react';
import { useBookingBasket } from '../../context/BookingBasketContext';

const pad = n => (n < 10 ? `0${n}` : `${n}`);

/**
 * mm:ss left until the deadline, or null when there is nothing to show.
 * Pure so the behaviour can be tested without mocking the clock.
 *
 * @param {string|null|undefined} timeoutDateTime ISO deadline
 * @param {number} nowMs current time in ms
 * @returns {string|null}
 */
export const formatRemaining = (timeoutDateTime, nowMs) => {
  if (!timeoutDateTime) return null;
  const deadline = new Date(timeoutDateTime).getTime();
  if (Number.isNaN(deadline)) return null;
  const remainingSeconds = Math.max(0, Math.floor((deadline - nowMs) / 1000));

  return `${pad(Math.floor(remainingSeconds / 60))}:${pad(remainingSeconds % 60)}`;
};

/**
 * Renders the remaining basket time as mm:ss.
 *
 * Derived from the absolute deadline on every tick rather than counted down in
 * component state, so an instance is always correct no matter how long it has
 * been mounted, and a missed or throttled tick self-corrects. react-countdown
 * latches itself dead here: componentWillUnmount sets mounted = false and
 * clears the timer, setTimeDeltaState returns early forever after, and start()
 * refuses to run again while the status is STARTED. Short-lived instances (the
 * drawer, the navbar dropdown) hid that because they are rebuilt on each open;
 * the tab bar instance stays mounted and stuck.
 */
const BookingTimeout = () => {
  const { timeoutDateTime } = useBookingBasket();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!timeoutDateTime) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [timeoutDateTime]);

  const remaining = formatRemaining(timeoutDateTime, now);
  if (remaining === null) return null;

  return <span>{remaining}</span>;
};

export default BookingTimeout;
